// Section 12 and 22 Health tab (stream M3b): the sick and injured with their treatment next
// season and last, death risk with its factors, healers and capacity, medicine on hand and
// demand, the illness multiplier and its levers (well, clinic, sick water carriers, crowding,
// quarantine), the triage policy and marks, and last season's illness and recovery lines.
// Numbers stay numbers; ids stay ids (the UI's text table labels them).
import {
  catalog,
  healthOverview,
  narrate,
  type CareOutcome,
  type Config,
  type Decision,
  type Factor,
  type GameState,
  type HealthCareOption,
  type IllnessId,
  type JobId,
  type Narration,
  type PersonId,
  type ReportEntry,
} from '../sim/api';

export interface HealthPatientRow {
  id: PersonId;
  name: string;
  ageYears: number;
  job: JobId;
  illnessId: IllnessId | null;
  injured: boolean;
  seasonsRemaining: number;
  seasonsSick: number;
  contagious: boolean;
  quarantined: boolean;
  /** Running the shorter treated course (had a full treatment). */
  treatedCourse: boolean;
  /** Chance of dying next season if untreated (0..1), with its factor list. */
  deathChance: number;
  deathFactors: Factor[];
  /** Chance of dying next season with the planned care. */
  plannedDeathChance: number;
  planned: CareOutcome;
  lastCare: CareOutcome | null;
  /** Triage mark: true treat first, false do not treat, null none (draft over standing). */
  mark: boolean | null;
  /** True when this season's draft changes the mark. */
  markPending: boolean;
  rank: number;
}

export interface HealthHealerRow {
  id: PersonId;
  name: string;
  slots: number;
  working: boolean;
  treatedLastSeason: string[];
}

/** A Section 12 lever: whether it is in place and what it does to illness. */
export interface HealthLever {
  id: 'well' | 'clinic' | 'sick-carrier' | 'crowding' | 'quarantine' | 'contagion';
  /** True when the lever is working for the settlement (well built, no sick carriers, ...). */
  good: boolean;
  /** The factor it contributed last season (1 when absent). */
  factor: number;
}

export interface HealthVM {
  policy: HealthCareOption;
  policyOptions: HealthCareOption[];
  policyPending: boolean;
  manual: boolean;
  canEdit: boolean;
  patients: HealthPatientRow[];
  healers: HealthHealerRow[];
  slots: number;
  capacity: number;
  visits: number;
  medicine: {
    onHand: number;
    capacity: number;
    demand: number;
    plannedUse: number;
    short: boolean;
  };
  multiplier: {
    /** Last season's as rolled, else the current state's. */
    value: number;
    factors: Factor[];
    asRolled: boolean;
    turn: number | null;
  };
  levers: HealthLever[];
  quarantine: {
    active: boolean;
    untilTurn: number | null;
    isolated: number;
    arrivalBoostUntilTurn: number | null;
  };
  sick: number;
  injured: number;
  /** Last season's illness, injury, recovery, treatment, quarantine and illness-death lines. */
  recent: Narration[];
}

const RECENT: readonly ReportEntry['kind'][] = [
  'illness',
  'injury',
  'recovery',
  'treatment',
  'passed-over',
  'quarantine',
  'death',
];

function isHealthEntry(e: ReportEntry): boolean {
  if (!RECENT.includes(e.kind)) return false;
  return e.kind !== 'death' || e.cause === 'illness' || e.cause === 'injury';
}

function factorOf(factors: readonly Factor[], test: (f: Factor) => boolean): number {
  let v = 1;
  for (const f of factors) if (f.op === 'mul' && test(f)) v *= f.value;
  return v;
}

function levers(factors: readonly Factor[], quarantineActive: boolean): HealthLever[] {
  const well = factorOf(factors, (f) => f.source === 'building:well');
  const clinic = factorOf(factors, (f) => f.source === 'building:clinic');
  const carriers = factorOf(factors, (f) => f.source === 'water:sick-carrier');
  const crowding = factorOf(
    factors,
    (f) => f.source === 'need:shelter' && (f.note ?? '').includes('over bed capacity'),
  );
  const contagion = factorOf(factors, (f) => f.source === 'illness:contagion');
  return [
    { id: 'well', good: well < 1, factor: well },
    { id: 'clinic', good: clinic < 1, factor: clinic },
    { id: 'sick-carrier', good: carriers <= 1, factor: carriers },
    { id: 'crowding', good: crowding <= 1, factor: crowding },
    { id: 'contagion', good: contagion <= 1, factor: contagion },
    { id: 'quarantine', good: quarantineActive, factor: 1 },
  ];
}

export function healthVM(
  game: GameState,
  draft: Decision | null,
  deciding: boolean,
  cfg?: Config,
): HealthVM {
  const o = healthOverview(game, draft, cfg);
  const perYear = catalog().seasonsPerYear;
  const person = (id: PersonId) => game.people[id] ?? null;
  const name = (id: PersonId): string => game.people[id]?.name ?? game.formerPeople[id]?.name ?? id;
  const options = (catalog().policies.find((p) => p.id === 'healthCare')?.options ??
    []) as HealthCareOption[];
  const last = game.history[game.history.length - 1] ?? null;
  const multiplier =
    o.lastSeason !== null && o.lastSeason.factors.length > 0
      ? {
          value: o.lastSeason.multiplier,
          factors: o.lastSeason.factors,
          asRolled: true,
          turn: o.lastSeason.turn,
        }
      : { value: o.current.value, factors: o.current.factors, asRolled: false, turn: null };
  return {
    policy: o.policy,
    policyOptions: options,
    policyPending: draft?.policies.healthCare !== undefined,
    manual: o.policy === 'manual',
    canEdit: deciding,
    patients: o.patients.map((p) => {
      const who = person(p.personId);
      return {
        id: p.personId,
        name: who?.name ?? p.personId,
        ageYears: Math.floor((who?.ageSeasons ?? 0) / perYear),
        job: who?.job ?? 'rest',
        illnessId: p.illnessId,
        injured: p.injured,
        seasonsRemaining: p.seasonsRemaining,
        seasonsSick: p.seasonsSick,
        contagious: p.contagious,
        quarantined: p.quarantined,
        treatedCourse: p.treatedCourse,
        deathChance: p.deathChance,
        deathFactors: p.deathFactors,
        plannedDeathChance: p.plannedDeathChance,
        planned: p.planned,
        lastCare: p.lastCare,
        mark: p.mark,
        markPending: draft?.triageOverrides[p.personId] !== undefined,
        rank: p.rank,
      };
    }),
    healers: o.healers.map((h) => ({
      id: h.personId,
      name: name(h.personId),
      slots: h.slots,
      working: h.working,
      treatedLastSeason: h.treatedLastSeason.map(name),
    })),
    slots: o.slots,
    capacity: o.capacity,
    visits: o.visits,
    medicine: o.medicine,
    multiplier,
    levers: levers(multiplier.factors, o.quarantine.active),
    quarantine: o.quarantine,
    sick: o.sick,
    injured: o.injured,
    recent: (last?.entries ?? [])
      .filter(isHealthEntry)
      .map((e) => narrate(e, game))
      .filter((n): n is Narration => n !== null),
  };
}
